import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Star, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReviewPage({ bookingId }: { bookingId: string }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState<'loading' | 'form' | 'submitted' | 'already' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/reviews/check/${bookingId}`);
        if (!res.ok) { setStatus('error'); setErrorMsg('Booking not found'); return; }
        const data = await res.json();
        setStatus(data.has_review ? 'already' : 'form');
      } catch { setStatus('error'); setErrorMsg('Could not connect to server'); }
    })();
  }, [bookingId]);

  const handleSubmit = async () => {
    if (rating === 0) return;
    try {
      const res = await fetch(`${API}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: bookingId, rating, comment }),
      });
      if (res.ok) { setStatus('submitted'); }
      else {
        const data = await res.json();
        setErrorMsg(data.detail || 'Failed to submit review');
        setStatus('error');
      }
    } catch { setErrorMsg('Could not connect to server'); setStatus('error'); }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50" data-testid="review-loading">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4" data-testid="review-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-xl">Angel Massage</CardTitle>
          <p className="text-sm text-muted-foreground">Rate your experience</p>
        </CardHeader>

        <CardContent className="space-y-4">
          {status === 'submitted' && (
            <div className="text-center space-y-3 py-6" data-testid="review-submitted">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
              <p className="font-medium text-lg">Thank you for your review!</p>
              <p className="text-sm text-muted-foreground">We appreciate your feedback and look forward to serving you again.</p>
            </div>
          )}

          {status === 'already' && (
            <div className="text-center space-y-3 py-6" data-testid="review-already">
              <Star className="w-12 h-12 text-amber-400 mx-auto fill-amber-400" />
              <p className="font-medium">You've already reviewed this booking</p>
              <p className="text-sm text-muted-foreground">Thank you for your feedback!</p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center space-y-3 py-6" data-testid="review-error">
              <p className="text-red-500 font-medium">{errorMsg}</p>
            </div>
          )}

          {status === 'form' && (
            <>
              <div className="flex justify-center gap-2 py-2" data-testid="review-stars">
                {[1, 2, 3, 4, 5].map(i => (
                  <button
                    key={i}
                    onMouseEnter={() => setHoverRating(i)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(i)}
                    data-testid={`star-${i}`}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star className={`w-8 h-8 ${i <= (hoverRating || rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-center text-sm text-muted-foreground">
                  {rating === 1 && 'Poor'}
                  {rating === 2 && 'Fair'}
                  {rating === 3 && 'Good'}
                  {rating === 4 && 'Very Good'}
                  {rating === 5 && 'Excellent'}
                </p>
              )}
              <Textarea
                placeholder="Share your experience (optional)"
                value={comment}
                onChange={e => setComment(e.target.value)}
                rows={3}
                data-testid="review-comment"
              />
              <Button
                className="w-full"
                onClick={handleSubmit}
                disabled={rating === 0}
                data-testid="submit-review-btn"
              >
                Submit Review
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
